/**
 * BadgeRenderer — badge
 * Standalone badge / chip with optional icon slot.
 */
import React from "react";
import { EditableText } from "../EditableText";
import { AnimatedField } from "../../animations/AnimatedField";
import { resolveText } from "../../utils/resolveText";

/* eslint-disable react/prop-types */

const VARIANT_STYLES = {
  default: {
    background: "var(--color-bg-surface-strong)",
    color: "var(--color-text-secondary)",
    border: "var(--stroke-thin) solid var(--color-border-subtle)",
  },
  accent: {
    background: "var(--color-bg-accent)",
    color: "var(--color-text-primary)",
    border: "none",
  },
  outline: {
    background: "transparent",
    color: "var(--color-text-accent)",
    border: "var(--stroke-thin) solid var(--color-text-accent)",
  },
};

export function BadgeRenderer({ element, editing, onChange, playing, isPaused, currentTimeSec }) {
  const label = resolveText(element.label || element.text || "");
  const variant = element.variant || "default";
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.default;
  // Animation applies to the badge as a whole; read from element-level or
  // the active label/text field.
  const animSource = element.label ?? element.text ?? element;
  const boxAnimation =
    (typeof animSource === "object" && animSource?.boxAnimation) ?? element.boxAnimation ?? null;
  const boxExitAnimation =
    (typeof animSource === "object" && animSource?.boxExitAnimation) ??
    element.boxExitAnimation ??
    null;

  return (
    <AnimatedField
      boxAnimation={boxAnimation}
      boxExitAnimation={boxExitAnimation}
      playing={playing}
      isPaused={isPaused}
      currentTimeSec={currentTimeSec}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "var(--space-1)",
          padding: "4px 14px",
          borderRadius: "var(--radius-full)",
          fontFamily: "var(--font-body)",
          fontSize: "var(--text-sm)",
          fontWeight: 600,
          letterSpacing: "0.04em",
          whiteSpace: "nowrap",
          ...styles,
        }}
      >
        <EditableText
          value={label}
          editing={editing}
          onChange={(val) => onChange(element.text ? "text" : "label", val)}
        />
      </span>
    </AnimatedField>
  );
}

export default BadgeRenderer;
